import { useEffect, useLayoutEffect, useRef } from "react";

/**
 * Gir fokus tilbake til elementet som hadde det før en dialog/palett ble
 * åpnet programmatisk (uten Radix `DialogTrigger`), så Esc ikke etterlater
 * fokus på <body> (VIB-2536).
 *
 * Fanger elementet i en layout-effekt når `open` blir true, dvs. før Radix
 * sin FocusScope flytter fokus inn i dialogen (den bruker en vanlig effekt).
 * Gjenoppretter i en timeout når `open` blir false, etter Radix sin egen
 * unmount-håndtering, og hopper over elementer som er fjernet fra DOM.
 */
export function useReturnFocus(open: boolean): void {
  const previous = useRef<HTMLElement | null>(null);

  useLayoutEffect(() => {
    if (!open) return;
    const el = document.activeElement;
    previous.current = el instanceof HTMLElement && el !== document.body ? el : null;
  }, [open]);

  useEffect(() => {
    if (open) return;
    const el = previous.current;
    if (!el) return;
    previous.current = null;
    const timer = setTimeout(() => {
      if (el.isConnected) el.focus();
    }, 0);
    return () => clearTimeout(timer);
  }, [open]);
}
